import React, { useRef } from "react";
import { MapPin, Phone, Clock, Mail, Wifi, Star, Trees, UtensilsCrossed, BedDouble, Car, Coffee, Heart, Mountain, Sun, Circle, Move, Download, FileText } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { txt, ensureFont, safeHref } from "@/lib/siteDefaults";

export const ICONS = { MapPin, Phone, Clock, Mail, Wifi, Star, Trees, UtensilsCrossed, BedDouble, Car, Coffee, Heart, Mountain, Sun };

const effCols = (cols, device) => (device === "mobile" ? 1 : device === "tablet" ? Math.min(cols, 2) : cols);
const scaleFont = (px, device) => (device === "mobile" && px > 28 ? Math.max(28, px * 0.62) : device === "tablet" && px > 40 ? px * 0.8 : px);

function Handle({ onPointerDown, className, title, children }) {
  return <div onPointerDown={onPointerDown} title={title} className={`absolute z-20 bg-gold text-forest-deep rounded-md shadow ${className}`} style={{ touchAction: "none" }}>{children}</div>;
}

function useDrag(onMove, onStart) {
  const st = useRef(null);
  return (e) => {
    e.preventDefault(); e.stopPropagation();
    onStart?.();
    st.current = { x: e.clientX, y: e.clientY };
    const move = (ev) => onMove(ev.clientX - st.current.x, ev.clientY - st.current.y);
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
}

function BlockView({ b, sid, lang, device, theme, edit, navigate }) {
  const s = b.style || {};
  const selected = edit?.selected?.bid === b.id;
  const ref = useRef(null);
  const start = useRef(null);
  ensureFont(s.font_family);
  const moveDrag = useDrag((dx, dy) => edit.update(sid, b.id, { style: { ...s, offset_x: Math.round(start.current.x + dx), offset_y: Math.round(start.current.y + dy) } }, false),
    () => { start.current = { x: s.offset_x || 0, y: s.offset_y || 0 }; edit.checkpoint(); });
  const sizeDrag = useDrag((dx, dy) => {
    const parentW = ref.current?.parentElement?.offsetWidth || 600;
    edit.update(sid, b.id, { style: { ...s, width: Math.min(100, Math.max(10, Math.round(start.current.w + (dx / parentW) * 100))), height: Math.max(40, Math.round(start.current.h + dy)) } }, false);
  }, () => { start.current = { w: s.width || 100, h: s.height || 300 }; edit.checkpoint(); });

  if (b.hidden && !edit) return null;
  const font = { fontFamily: s.font_family ? `'${s.font_family}', serif` : undefined, fontSize: s.font_size ? scaleFont(s.font_size, device) : undefined,
    color: s.color || undefined, fontWeight: s.bold ? 700 : undefined, fontStyle: s.italic ? "italic" : undefined, textAlign: s.align || undefined, lineHeight: s.line_height || undefined };
  const wrap = {
    position: "relative", marginTop: s.mt || 0, marginBottom: s.mb ?? 0, opacity: b.hidden ? 0.35 : s.opacity != null ? s.opacity : 1,
    transform: s.offset_x || s.offset_y ? `translate(${device === "mobile" ? 0 : s.offset_x || 0}px, ${s.offset_y || 0}px)` : undefined,
    width: b.type !== "image" && s.width && device !== "mobile" ? `${s.width}%` : undefined,
    outline: selected ? "2px solid #C5A059" : edit ? "1px dashed transparent" : undefined, outlineOffset: 3,
    marginLeft: s.align === "center" && s.width ? "auto" : undefined, marginRight: s.align === "center" && s.width ? "auto" : undefined,
  };
  const editable = selected && ["heading", "text", "button", "icon"].includes(b.type);
  const textProps = editable ? {
    contentEditable: true, suppressContentEditableWarning: true, "data-testid": "editor-inline-text",
    onBlur: (e) => edit.update(sid, b.id, { text: { ...b.text, [lang]: e.currentTarget.innerText } }),
    style: { outline: "none", cursor: "text" },
  } : {};
  const onClick = edit ? (e) => { e.stopPropagation(); edit.select({ scope: edit.scope, sid, bid: b.id }); } : undefined;
  const value = txt(b.text, lang);

  let inner;
  switch (b.type) {
    case "heading": {
      const Tag = s.level || "h2";
      inner = <Tag className="font-display" style={{ ...font, fontWeight: s.bold === false ? 400 : 600, margin: 0, fontFamily: font.fontFamily || `'${theme.heading_font}', serif` }}><span {...textProps}>{value}</span></Tag>;
      break;
    }
    case "text":
      inner = <p style={{ ...font, margin: 0, whiteSpace: "pre-line" }}><span {...textProps}>{value}</span></p>;
      break;
    case "button": {
      const href = safeHref(b.href);
      const go = (e) => { if (edit) return; e.preventDefault(); if (/^(https?:|mailto:|tel:)/i.test(href)) window.open(href, "_blank", "noopener"); else if (href !== "#") navigate?.(href); };
      inner = (
        <a href={href} onClick={go} data-testid="site-button"
          className="inline-flex items-center justify-center h-12 px-7 transition-transform hover:-translate-y-0.5"
          style={{ background: s.bg_color || theme.accent, color: s.text_color || theme.text, borderRadius: s.radius ?? 12, fontSize: font.fontSize, fontWeight: 600, border: s.border ? `1px solid ${s.border}` : "none", fontFamily: font.fontFamily }}>
          <span {...textProps}>{value}</span>
        </a>
      );
      break;
    }
    case "image":
      inner = (
        <div style={{ width: `${device === "mobile" ? 100 : s.width || 100}%`, marginLeft: s.align === "center" ? "auto" : s.align === "right" ? "auto" : undefined, marginRight: s.align === "center" ? "auto" : undefined }}>
          <img src={mediaUrl(b.src)} alt={b.alt || ""} loading="lazy" draggable={false}
            style={{ width: "100%", height: s.height ? (device === "mobile" ? Math.min(s.height, 320) : s.height) : "auto", objectFit: "cover", objectPosition: s.object_position || "50% 50%", borderRadius: s.radius ?? 16, display: "block", filter: s.brightness != null && s.brightness !== 100 ? `brightness(${s.brightness}%)` : undefined }} />
        </div>
      );
      break;
    case "divider":
      inner = <div style={{ width: s.width || 96, height: 2, background: `linear-gradient(90deg, ${s.color || theme.accent}, transparent)`, marginLeft: s.align === "center" ? "auto" : undefined, marginRight: s.align === "center" ? "auto" : undefined }} />;
      break;
    case "spacer":
      inner = <div style={{ height: s.height || 40 }} />;
      break;
    case "icon": {
      const Ico = ICONS[b.icon] || Circle;
      inner = <p style={{ ...font, margin: 0, display: "flex", alignItems: "center", gap: 10, justifyContent: s.align === "center" ? "center" : undefined }}><Ico style={{ color: s.icon_color || theme.accent, width: 20, height: 20, flexShrink: 0 }} /><span {...textProps}>{value}</span></p>;
      break;
    }
    case "file": {
      const margin = { marginLeft: s.align === "left" ? 0 : "auto", marginRight: s.align === "right" ? 0 : "auto" };
      inner = !b.pages?.length ? (edit ? (
        <div className="border-2 border-dashed border-gold/50 rounded-xl p-10 text-center text-forest/60 flex flex-col items-center gap-2" style={{ maxWidth: s.max_width || 900, ...margin }}>
          <FileText className="w-8 h-8" />Качете PDF или снимка A4 от панела вдясно
        </div>) : null) : (
        <div style={{ maxWidth: s.max_width || 900, ...margin }} data-testid="site-document">
          <div className="space-y-4">
            {b.pages.map((p, i) => <img key={p} src={mediaUrl(p)} alt={`${b.name || "document"} ${i + 1}`} loading={i ? "lazy" : "eager"} draggable={false}
              className="w-full bg-white shadow-lg block" style={{ borderRadius: s.radius ?? 6 }} data-testid="site-document-page" />)}
          </div>
          {s.show_download !== false && (
            <div className="mt-4 flex" style={{ justifyContent: s.align === "left" ? "flex-start" : s.align === "right" ? "flex-end" : "center" }}>
              <a href={`${mediaUrl(b.src)}?download=1`} onClick={edit ? (e) => e.preventDefault() : undefined} data-testid="site-document-download"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-xl font-semibold transition-transform hover:-translate-y-0.5" style={{ background: theme.accent, color: theme.text }}>
                <Download className="w-4 h-4" /> {value}
              </a>
            </div>
          )}
        </div>
      );
      break;
    }
    default:
      inner = null;
  }
  return (
    <div ref={ref} style={wrap} onClick={onClick} data-testid={`site-block-${b.type}`} data-block-id={b.id}>
      {inner}
      {selected && <Handle onPointerDown={moveDrag} className="-left-3 -top-3 p-1 cursor-move" title="Влачете, за да преместите"><Move className="w-3.5 h-3.5" /></Handle>}
      {selected && b.type === "image" && <Handle onPointerDown={sizeDrag} className="-right-2 -bottom-2 w-4 h-4 cursor-se-resize" title="Влачете, за да промените размера" />}
    </div>
  );
}

const groupInline = (blocks) => blocks.reduce((acc, b) => {
  const last = acc[acc.length - 1];
  if (b.style?.inline && Array.isArray(last)) last.push(b);
  else acc.push(b.style?.inline ? [b] : b);
  return acc;
}, []);

function SectionView({ sec, lang, device, theme, edit, dynamic, navigate }) {
  const s = sec.style;
  if (sec.hidden && !edit) return null;
  const selected = edit?.selected?.sid === sec.id && !edit.selected.bid;
  const onClick = edit ? (e) => { e.stopPropagation(); edit.select({ scope: edit.scope, sid: sec.id }); } : undefined;
  const cols = effCols(s.columns || 1, device);
  const blocks = sec.blocks || [];
  const columns = Array.from({ length: cols }, (_, i) => blocks.filter((b) => Math.min(b.col || 0, (s.columns || 1) - 1) % cols === i || (cols === 1)));
  const colsToRender = cols === 1 ? [blocks.slice().sort((a, b2) => (a.col || 0) - (b2.col || 0))] : columns;
  const renderBlocks = (list) => groupInline(list).map((item) => (Array.isArray(item)
    ? <div key={item[0].id} className="flex flex-wrap gap-4" style={{ justifyContent: item[0].style?.align === "center" ? "center" : undefined }}>{item.map((b) => <BlockView key={b.id} b={b} sid={sec.id} lang={lang} device={device} theme={theme} edit={edit} navigate={navigate} />)}</div>
    : <BlockView key={item.id} b={item} sid={sec.id} lang={lang} device={device} theme={theme} edit={edit} navigate={navigate} />));

  return (
    <section onClick={onClick} data-testid={`site-section-${sec.id}`}
      style={{ position: "relative", background: s.bg_color || undefined, color: s.text_color || undefined, minHeight: s.min_height ? `${s.min_height}vh` : undefined,
        display: s.min_height ? "flex" : undefined, alignItems: { start: "flex-start", center: "center", end: "flex-end" }[s.valign] || undefined,
        outline: selected ? "3px solid #C5A059" : undefined, outlineOffset: -3, opacity: sec.hidden ? 0.45 : 1, overflow: "hidden" }}>
      {s.bg_image && <img src={mediaUrl(s.bg_image)} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: s.bg_position || "50% 50%", filter: s.brightness !== 100 ? `brightness(${s.brightness}%)` : undefined }} />}
      {(s.bg_image || s.overlay_opacity > 0) && s.overlay_opacity > 0 && <div className="absolute inset-0" style={{ background: s.overlay_color || "#000", opacity: s.overlay_opacity }} />}
      {sec.type === "dynamic" ? (
        <div className="relative w-full" style={{ pointerEvents: edit ? "none" : undefined }}>{dynamic?.[sec.dynamic]}</div>
      ) : (
        <div className="relative w-full mx-auto px-4" style={{ maxWidth: s.max_width || 1280, paddingTop: device === "mobile" ? Math.min(s.padding_y, 48) : s.padding_y, paddingBottom: device === "mobile" ? Math.min(s.padding_y, 48) : s.padding_y }}>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${colsToRender.length}, minmax(0, 1fr))`, gap: s.gap ?? 40, alignItems: { start: "start", center: "center", end: "end" }[s.valign] || "start" }}>
            {colsToRender.map((list, i) => <div key={i} className="min-w-0">{renderBlocks(list)}</div>)}
          </div>
        </div>
      )}
      {edit && sec.hidden && <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">Скрита секция</div>}
    </section>
  );
}

export function SiteSections({ sections, ...rest }) {
  return <>{(sections || []).map((sec) => <SectionView key={sec.id} sec={sec} {...rest} />)}</>;
}
